import { Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';

import { Observable, ReplaySubject, Subject, distinctUntilChanged, takeUntil } from 'rxjs';

import { parseJsonString } from '../helpers';
import { isEmptyObject, jsonEqual, removeEmptyObjectProps } from '../helpers/object-utils';

import { ListStateParams } from './list-state.types';

@Injectable()
export class ListStateService {
  /**
   *  listState is a local storage of list state params.
   *  Is set from the URL queryParams and keeps the actual list state.
   */
  private listState: { [key: string]: ListStateParams } = {};

  private prevListState: { [key: string]: ListStateParams } = {};

  /**
   *  listStateUrlParamsSubject$ - list state params in a Subject based/subscribed on URL queryParams
   */
  private readonly listStateUrlParamsSubject$ = new ReplaySubject<ListStateParams>(1);

  private ngUnsubscribe$: Subject<void>;

  get listStateUrlParams$(): Observable<ListStateParams> {
    return this.listStateUrlParamsSubject$.pipe(distinctUntilChanged(jsonEqual));
  }

  get listStateParams(): ListStateParams {
    return { ...this.listState };
  }

  get prevListStateParams(): ListStateParams {
    return { ...this.prevListState };
  }

  constructor(private readonly router: Router, private readonly route: ActivatedRoute) {}

  public unsubscribeUrlState(): void {
    this.listStateUrlParamsSubject$.next({});
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  public setListState(params: ListStateParams, listStateKey: string): void {
    // deleting empty params from queryParams
    const emptyParams = {
      typeGroup: null,
      filters: null,
      sorting: null,
      query: null
    };
    this.prevListState[listStateKey] = { ...this.listState[listStateKey] };
    this.listState[listStateKey] = removeEmptyObjectProps({ ...this.listStateParams[listStateKey], ...params });
    this.router.navigate([], {
      queryParams: { ...emptyParams, ...this.serializeParams(this.listStateParams[listStateKey]) },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  public subscribeOnStateFromUrl(listStateKey: string, includeEmptyParams = false): void {
    this.ngUnsubscribe$ = new Subject<void>();
    this.route.queryParams.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(urlParams => {
      if (!isEmptyObject(urlParams) || includeEmptyParams) {
        this.listState[listStateKey] = this.deserializeParams(urlParams);
        this.listStateUrlParamsSubject$.next(this.listStateParams[listStateKey]);
      }
    });
  }

  private deserializeParams(queryParams: Params): ListStateParams {
    return Object.entries(queryParams).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: parseJsonString(value)
      }),
      {} as ListStateParams
    );
  }

  private serializeParams(queryParams: ListStateParams): Params {
    return Object.entries(queryParams).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: JSON.stringify(value)
      }),
      {} as Params
    );
  }
}
